import { Controller } from "@hotwired/stimulus"

// Selector de emoji para el icono (badge) de un menu item.
// Replica BadgeLabel + BadgeLabelModal del frontend (emoji-mart).
//
// El badge se guarda como JSON en un hidden field: { "emoji": { "native": "😀" } }.
// El boton add-on muestra el ProductBadge actual (emoji o icono FA por tipo) y
// abre un popover con el picker de emoji-mart (lazy import al primer uso).
//
// El panel se portea a <body> con position: fixed para no recortarse contra el
// overflow del modal (modal-panel overflow-hidden + cuerpo overflow-y-auto). Como
// queda fuera del subarbol del controller, NO se usa this.panelTarget despues de
// moverlo: se cachea la referencia en this._panel.
//
// Uso:
//   div data-controller="manage--emoji-picker"
//     input hidden data-manage--emoji-picker-target="hidden"
//     button data-action="manage--emoji-picker#toggle"
//       div data-manage--emoji-picker-target="preview" (ProductBadge)
//     div data-manage--emoji-picker-target="panel" (popover, hidden)
export default class extends Controller {
  static targets = ["hidden", "preview", "panel"]

  connect() {
    this._modules = null
    this._panel = this.panelTarget
    this._trigger = this.element.querySelector("[data-action*='manage--emoji-picker#toggle']")
    this._onOutsideClick = this._onOutsideClick.bind(this)
    this._reposition = this._reposition.bind(this)
  }

  disconnect() {
    this._teardownListeners()
    if (this._panel) this._panel.remove()
  }

  async toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this._isOpen()) {
      this.close()
      return
    }

    // El panel debe estar visible ANTES de instanciar emoji-mart: el picker
    // mide su contenedor al montarse y con display:none renderiza tamaño 0.
    document.body.appendChild(this._panel)
    this._panel.style.position = "fixed"
    this._panel.style.zIndex = "9999"
    this._panel.style.left = "-9999px"
    this._panel.style.top = "0px"
    this._panel.classList.remove("hidden")

    await this._mountPicker()
    this._reposition()

    setTimeout(() => {
      document.addEventListener("click", this._onOutsideClick)
      window.addEventListener("resize", this._reposition)
      window.addEventListener("scroll", this._reposition, true)
    }, 0)
  }

  close() {
    if (!this._isOpen()) return
    this._panel.classList.add("hidden")
    this._teardownListeners()
  }

  _teardownListeners() {
    document.removeEventListener("click", this._onOutsideClick)
    window.removeEventListener("resize", this._reposition)
    window.removeEventListener("scroll", this._reposition, true)
  }

  _isOpen() {
    return this._panel && !this._panel.classList.contains("hidden")
  }

  _reposition() {
    if (!this._trigger || !this._panel) return

    const rect = this._trigger.getBoundingClientRect()
    const panel = this._panel
    const width = panel.offsetWidth || 352
    const height = panel.offsetHeight || 435
    const margin = 6

    let left = rect.left
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin
    }
    if (left < margin) left = margin

    let top = rect.bottom + margin
    if (top + height > window.innerHeight - margin) {
      const above = rect.top - height - margin
      top = above >= margin ? above : Math.max(margin, window.innerHeight - height - margin)
    }

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  _onOutsideClick(event) {
    if (this.element.contains(event.target)) return
    if (this._panel.contains(event.target)) return
    this.close()
  }

  // emoji-mart calcula dimensiones al montarse y, si se oculta con display:none
  // y se reabre, renderiza vacio. Por eso recreamos el picker en cada apertura
  // (con el panel ya visible). Los modulos importados se cachean.
  async _mountPicker() {
    if (!this._modules) {
      const [pickerModule, dataModule] = await Promise.all([
        import("emoji-mart"),
        import("@emoji-mart/data")
      ])
      this._modules = { Picker: pickerModule.Picker, data: dataModule.default }
    }

    this._panel.replaceChildren()
    const picker = new this._modules.Picker({
      data: this._modules.data,
      theme: "light",
      previewPosition: "none",
      onEmojiSelect: (emoji) => this._select(emoji)
    })
    this._panel.appendChild(picker)
  }

  _select(emoji) {
    const native = emoji?.native
    if (!native) return

    this.hiddenTarget.value = JSON.stringify({ emoji: { native } })
    this.previewTarget.innerHTML =
      `<div class="absolute inset-0 flex items-center justify-center">` +
      `<div class="text-[21px]" style="font-family: Arial;">${native}</div>` +
      `</div>`
    this.close()
  }
}
